/**
 * retryClassification — is a FAILED write TRANSIENT (safe to retry / queue for
 * replay) or DETERMINISTIC (it will fail identically forever → retrying is
 * guaranteed silent loss)?
 *
 * WHY this exists (BUG-FE-OFFLINE-RETRY-DETERMINISTIC-SILENT-LOSS): the offline
 * replay loop retried EVERY failed entry up to maxRetries, THEN pinned it
 * 'failed'. For a DETERMINISTIC failure — a 400/422 client-input defect, or a
 * ContractMismatch envelope-drift — retry #5 fails identically to retry #1, so
 * the rep sees nothing for 5 flush cycles, then a silent
 * 'offline-retries-exhausted' pin. Industry standard: retry only TRANSIENT
 * failures (5xx / network / timeout / rate-limit); a deterministic failure must
 * fail LOUD immediately. `isRetryable` is that single SSoT, for EVERY door.
 *
 * ⚠ THE SET IS NOW AN EXPLICIT *RETRYABLE* SET (TASK-FE-V8-DOOR-OWNERSHIP-REFUSALS-
 * 2026-10-08, AC 7 — it replaces the old {400, 422} deny-list that kept 403/404/409
 * replayable under FR-FE-OFFLINE-FIRST-WRITE-STANDARD-2026-06-09). RETRYABLE: no
 * HTTP status (network / transport / timeout), 408, 429, every 5xx, and 401 (see
 * below). EVERY OTHER 4xx IS FINAL: the server evaluated this exact body and said
 * no; the source_event_id makes a re-send the same request, so it gets the same
 * answer. Retrying a 403 ownership refusal (ENGAGEMENT_NOT_OWNED — the only ownership code on
 * the wire; DEAD_LETTER_NOT_OWNED is the database's raise token and surfaces under
 * error.upstream.message, never as error.code — and the v8 door's SL #2682 codes) five times only delayed the
 * loud failure and told the rep "wird erneut gesendet" for a report that never
 * would be. A final entry is never lost: it is pinned and KEPT on the device,
 * captured off-device once, and discardable by the rep (queueExit).
 *
 * ⚠ 401 IS NOT FINAL, AND IT IS NOT A BLIP EITHER. serviceFetch already spent its
 * ONE re-handoff (serviceLayerClient.ts) before a 401 can reach this classifier, so
 * a 401 here means "the rep must log in again". It stays retryable (queued at
 * submit, kept on the device) and the replay layer holds it as 'auth_required'
 * without spending the retry budget (replayFailure.ts, `isAuthRequired`).
 *
 * ⚠ THE OUTER STATUS IS NOT ALWAYS THE VERDICT (BUG-2026-07-21-S1787): SL wraps a
 * failing database/downstream call as a GENERIC outer 5xx — e.g.
 * `502 VISIT_REPORT_V2_RPC_FAILED` — whose envelope carries the REAL cause under
 * `details.upstream.{code,message}`. A `23514` (Postgres CHECK-constraint violation)
 * or a 4xx downstream code is DETERMINISTIC: the payload is rejected identically on
 * every replay, so classifying by the transient outer 502 alone made the entry loop
 * forever (11–25 days on a rep's phone: "re-pended 3 stranded … 0 synced, 0 failed").
 * `isDeterministicUpstream` inspects that cause; the upstream verdict WINS over the
 * outer status. It stays NARROW (see its docstring) so a genuine transient RPC blip
 * with no structured upstream still replays — over-classifying would re-introduce the
 * data loss this subsystem exists to prevent, from the other direction (D-33 R8).
 */

import { ContractMismatchError } from '../http/slErrors'

/**
 * The ONLY 4xx statuses a re-send can turn into a success: 408 (timeout) and 429
 * (rate-limit) are transient by definition; 401 is "log in again", kept for after
 * the login (module header). Every other 4xx is FINAL.
 */
const RETRYABLE_4XX_STATUSES: ReadonlySet<number> = new Set([401, 408, 429])

/** HTTP 401 — the session outlived serviceFetch's one re-handoff. */
const AUTH_REQUIRED_STATUS = 401

/**
 * The 403s that are NOT a verdict on the body: the server refused the REQUEST before it looked at
 * the write, so the identical write can succeed once the cause is gone. They are RETRIED WITH
 * BACKOFF like any transient failure (the retry budget still applies — one that never clears ends
 * loud, as an ordinary failed entry with "Erneut versuchen", never pinned final). Every other 403
 * stays FINAL.
 *  - CSRF_FAILED (Codex P1 #2213): the X-CSRF-Token header was missing or stale. Every request reads
 *    the token afresh (serviceLayerClient.headers). No re-handoff is triggered on a 403 here; that
 *    flow is a separate row. ⚠ UNDECLARED in SL's error contract (SL BUG-2026-10-09-FJ5PL): a string
 *    match on what the gateway sends today.
 *  - ORIGIN_NOT_ALLOWED (SL #2721, declared in the OpenAPI as MiddlewareErrorCode): the request's
 *    Origin is not in the server's trust list. From the app it can only mean a server-side trust-list
 *    misconfiguration — a report must not be pinned as refused for that.
 */
const NOT_A_VERDICT_STATUS = 403
const NOT_A_VERDICT_403_CODES: ReadonlySet<string> = new Set(['CSRF_FAILED', 'ORIGIN_NOT_ALLOWED'])

function isRequestRefusedBeforeTheWrite(err: unknown, status: number): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code
  return status === NOT_A_VERDICT_STATUS && typeof code === 'string' && NOT_A_VERDICT_403_CODES.has(code)
}

/**
 * True when the server itself DECLARES the failure retryable: `error.retryable: true` in the SL
 * envelope (the parser lifts it to `err.details.retryable`). On a 5xx its word WINS over the
 * upstream-code heuristic below: SL's park failures (`502 VISIT_REPORT_PARK_RACE` / `…_PARK_FAILED`,
 * 9HF2A) are declared safe to re-send byte-identically, and may still carry a 4xx or class-23
 * `details.upstream.code` that the heuristic alone would read as "rejected forever" and pin FINAL.
 * Strictly `=== true`: an absent, false or non-boolean value leaves the heuristic in charge.
 */
function isDeclaredRetryable(err: unknown): boolean {
  const details = (err as { details?: unknown } | null | undefined)?.details
  return details !== null && typeof details === 'object' && (details as { retryable?: unknown }).retryable === true
}

/**
 * The numeric HTTP status carried by a thrown SL error, if any. `SLApiError`
 * exposes `.status` (slErrors.ts); transport errors (`ServiceHttpError`
 * 'INTERNAL'/'TIMEOUT', iOS Safari "Load failed", a raw network TypeError)
 * carry NO numeric status → `undefined`, which classifies as retryable.
 */
export function httpStatusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown } | null | undefined)?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * The `upstream.code` a wrapped-RPC error carries, normalised to a string, or
 * `undefined` when there is no structured upstream cause.
 *
 * SL wraps a failing database/downstream call as an outer 5xx whose envelope
 * carries `details.upstream = { code, message }`; the errorEnvelope parser lifts
 * that sibling extra into `err.details` via `.passthrough()`. The code arrives as
 * either a Postgres SQLSTATE string (`'23514'`) or a numeric HTTP-ish code (`422`),
 * so both shapes are normalised to a string here. Shape-guarded end to end — a
 * malformed or partial envelope yields `undefined`, never a throw.
 */
function upstreamCodeOf(err: unknown): string | undefined {
  const details = (err as { details?: unknown } | null | undefined)?.details
  if (details === null || typeof details !== 'object') return undefined
  const upstream = (details as { upstream?: unknown }).upstream
  if (upstream === null || typeof upstream !== 'object') return undefined
  const code = (upstream as { code?: unknown }).code
  if (typeof code === 'string') return code
  if (typeof code === 'number' && Number.isFinite(code)) return String(code)
  return undefined
}

/**
 * True when a failure carries a DETERMINISTIC cause behind a transient OUTER status
 * — the S1787 class (BUG-2026-07-21-S1787). The outer status of a wrapped-RPC
 * failure is a generic `502 …_RPC_FAILED`, which `isRetryable` would otherwise treat
 * as a server-transient 5xx and replay forever; but the upstream code proves the
 * write is rejected by the PAYLOAD itself, which blind replay can NEVER satisfy:
 *
 *   - a Postgres CONSTRAINT-violation SQLSTATE — class 23 (check / not-null / FK /
 *     unique), e.g. `23514 ck_evd_branche_correction`. A row that violates a
 *     constraint violates it identically on every replay. Scoped to class 23
 *     DELIBERATELY: it is the unambiguously-deterministic Postgres class. Other
 *     classes are NOT swept in — e.g. `40001` serialization_failure / `40P01`
 *     deadlock_detected are TRANSIENT and a retry clears them, so a blanket
 *     "any SQLSTATE is deterministic" would wrongly dead-letter recoverable writes.
 *   - a 4xx-class upstream HTTP code — the downstream service rejected the request
 *     AS SHAPED — EXCEPT 408 (timeout) and 429 (rate-limit), the two 4xx codes that
 *     are transient by definition (mirrors RETRYABLE_4XX_STATUSES' own reasoning).
 *
 * NARROW BY DESIGN: with no structured `details.upstream.code`, a plain 502 stays
 * retryable — a genuine transient RPC blip at the border must still replay
 * (offline-first). Over-classifying here re-introduces the data loss this subsystem
 * exists to prevent, from the other direction (D-33 R8 — the twin of the loop).
 */
export function isDeterministicUpstream(err: unknown): boolean {
  const code = upstreamCodeOf(err)
  if (code === undefined) return false
  // Postgres SQLSTATE class 23 (integrity constraint violation): exactly 5 chars,
  // '23' + 3 alphanumerics — covers 23502/23503/23505/23514 and 23P01.
  if (/^23[0-9A-Za-z]{3}$/.test(code)) return true
  // A 4xx-class downstream HTTP code — a deterministic rejection of the body — save
  // the two transient-by-definition 4xx (408 timeout, 429 rate-limit).
  const numeric = Number(code)
  if (
    Number.isInteger(numeric) &&
    numeric >= 400 && numeric <= 499 &&
    numeric !== 408 && numeric !== 429
  ) {
    return true
  }
  return false
}

/**
 * True when a failed write is TRANSIENT and SHOULD be retried / queued for
 * replay; false when it is DETERMINISTIC and must fail loud now.
 *
 * RETRYABLE (true): network / transport / timeout / no-status; 401 (auth — held for
 *   the next login, see `isAuthRequired`), 408, 429; a 403 whose code is CSRF_FAILED or
 *   ORIGIN_NOT_ALLOWED; every 5xx WITHOUT a deterministic upstream cause; and every 5xx the
 *   server DECLARES retryable (`error.retryable: true`), whatever its upstream cause.
 * FINAL (false): every other status — every other 4xx (400 403 404 409 422 …), any
 *   `ContractMismatchError`, and any failure carrying a deterministic upstream cause
 *   (S1787 — a 5xx wrapping a Postgres class-23 SQLSTATE or a 4xx downstream code).
 */
export function isRetryable(err: unknown): boolean {
  // Deterministic envelope-shape drift — Pillar-2 zod safeParse rejected the
  // wire shape; it will reject identically on every replay. Fail loud now.
  if (
    err instanceof ContractMismatchError ||
    (err as { name?: unknown } | null | undefined)?.name === 'ContractMismatchError'
  ) {
    return false
  }

  // Deterministic cause behind a transient outer status (S1787): a 502 …_RPC_FAILED
  // wrapping a Postgres 23514 check-constraint violation reads as a retryable 5xx by
  // its outer status, but the payload is rejected forever. The upstream verdict WINS —
  // unless the server DECLARED this 5xx retryable (isDeclaredRetryable): then its word does.
  const status = httpStatusOf(err)
  const serverError = status !== undefined && status >= 500 && status <= 599
  if (serverError && isDeclaredRetryable(err)) return true
  if (isDeterministicUpstream(err)) return false

  // No numeric HTTP status → transport / timeout / dropped connection → retry.
  if (status === undefined) return true
  // Server-transient: every 5xx (a deterministic upstream behind it was refused above).
  if (serverError) return true
  // Refused before the write was looked at (see NOT_A_VERDICT_403_CODES).
  if (isRequestRefusedBeforeTheWrite(err, status)) return true
  // The explicit retryable 4xx; anything else — every other 4xx, and any status outside
  // the error classes a throw can carry — is FINAL.
  return RETRYABLE_4XX_STATUSES.has(status)
}

/**
 * The label a FINAL refusal is pinned with (QueueEntry.finalCode): "HTTP <status> <CODE>", either
 * part alone when only one is carried, else the error's class name. The SAME format as the rep copy's
 * visitReportRefusalLabelOf (api/missionVisitReports) — kept here because the outbox layer must not
 * import the api layer (it pulls serviceFetch in). 'UNKNOWN_REFUSAL' names a throw that is not an
 * Error at all: the entry still records that the server's verdict was unreadable, never an empty code.
 */
export function refusalCodeOf(err: unknown): string {
  const e = err as { code?: unknown; name?: unknown } | null | undefined
  const status = httpStatusOf(err)
  const parts = [
    status === undefined ? null : `HTTP ${status}`,
    typeof e?.code === 'string' && e.code !== '' ? e.code : null,
  ].filter((p): p is string => p !== null)
  if (parts.length > 0) return parts.join(' ')
  return typeof e?.name === 'string' && e.name !== '' ? e.name : 'UNKNOWN_REFUSAL'
}

/**
 * True when a failed write needs the rep to LOG IN again: an HTTP 401 that outlived
 * serviceFetch's one re-handoff. Not final and not a transient retry — the replay layer
 * keeps the entry as 'auth_required' (no retry spent) and the door sinks say "bitte
 * erneut anmelden". A 403 is NOT this: it is a refusal on a valid session (FINAL).
 */
export function isAuthRequired(err: unknown): boolean {
  return httpStatusOf(err) === AUTH_REQUIRED_STATUS
}
